export const CATEGORIES_GUIDE = `
# VoiceThera Categories Guide

VoiceThera offers diverse wellness categories, each designed for specific needs and therapeutic approaches. This guide helps you choose the right category and understand what makes each one unique.

## Category Overview

### Standard Categories (Included in All Plans)

**Meditation** - Classic mindfulness and meditation practices
**Anxiety & Stress Relief** - Calm your nervous system and reduce overwhelm
**Sleep** - Fall asleep faster and sleep more deeply
**Massage** - Guided body relaxation and tension release
**Motivation** - Unlock drive, ambition, and forward momentum
**Memory** - Enhance cognitive function and mental clarity
**Fitness** - Mental preparation for physical performance

### Premium Categories (Included in All Plans)

**ZEN AF** - Raw, authentic, no-BS therapeutic sessions
**News Meditation** - Transform daily news into mindfulness practice

## Category Deep Dives

### 1. Meditation

**Best for:**
- Daily mindfulness practice
- Building meditation habit
- General stress management
- Cultivating inner peace
- Present-moment awareness

**Session Style:**
- Calm, grounding voice
- Traditional meditation techniques
- Focus on breath, body, and awareness
- Gentle guidance with silence intervals
- Suitable for beginners and experienced meditators

**When to Use:**
- Morning centering practice
- Midday reset
- Evening wind-down
- Anytime you need to return to baseline

**Sample Prompt Ideas:**
- "I need a body scan meditation to release tension"
- "Help me cultivate compassion for myself and others"
- "Guide me through observing thoughts without judgment"
- "I want to practice loving-kindness meditation"

**Duration Recommendations:**
- **1-3 minutes:** Quick centering during busy day
- **5-7 minutes:** Standard daily practice
- **10-15 minutes:** Deep meditative experience

---

### 2. Anxiety & Stress Relief

**Best for:**
- Acute anxiety or panic
- Work stress and overwhelm
- Pre-event nervousness
- Racing thoughts
- Physical tension from stress

**Session Style:**
- Calming, reassuring voice
- Evidence-based anxiety reduction techniques
- Nervous system regulation
- Cognitive reframing
- Immediate relief focus

**When to Use:**
- During panic attacks or acute anxiety
- Before stressful events (presentations, confrontations)
- After overwhelming days
- When stress is manifesting physically

**Sample Prompt Ideas:**
- "I have a presentation in an hour and I'm spiraling"
- "My boss criticized me and now I'm overthinking everything"
- "I can't stop worrying about money/health/relationships"
- "I feel overwhelmed by my to-do list and don't know where to start"

**Duration Recommendations:**
- **1-3 minutes:** Emergency anxiety intervention
- **5-7 minutes:** Thorough stress relief
- **10-15 minutes:** Deep nervous system reset

**Pro Tip:** Bookmark anxiety sessions you generate during acute moments - replay them when similar situations arise.

---

### 3. Sleep

**Best for:**
- Difficulty falling asleep
- Racing mind at bedtime
- Insomnia
- Poor sleep quality
- Nighttime anxiety

**Session Style:**
- Soothing, slow-paced narration
- Progressive relaxation techniques
- Sleep-inducing visualizations
- Voice gradually quieting
- Designed to guide you from wakefulness to sleep

**When to Use:**
- At bedtime, in bed with lights off
- When you wake during the night and can't fall back asleep
- During insomnia episodes
- For naps (shorter sessions)

**Sample Prompt Ideas:**
- "I can't stop thinking about work and I need to sleep"
- "Help me release the day and surrender to rest"
- "I'm anxious about tomorrow and it's keeping me awake"
- "Guide me into deep, restorative sleep"

**Duration Recommendations:**
- **5-7 minutes:** Quick sleep induction
- **10-15 minutes:** Full bedtime wind-down

**Pro Tip:** Use comfortable headphones or play on speaker at low volume. Session doesn't need to finish - you're meant to fall asleep during it.

---

### 4. Massage (Guided Body Relaxation)

**Best for:**
- Physical tension and pain
- Body awareness practice
- Releasing stored stress
- Chronic pain management
- Somatic healing

**Session Style:**
- Slow, deliberate guidance
- Progressive muscle relaxation
- Body scanning techniques
- Focus on physical sensations
- Release of muscular tension

**When to Use:**
- End of physically demanding day
- When experiencing tension headaches, back pain, or tightness
- Before or after workouts
- During recovery from illness or injury
- For chronic pain relief

**Sample Prompt Ideas:**
- "My shoulders and neck are killing me from computer work"
- "I hold stress in my jaw and need to release it"
- "My lower back is tight from sitting all day"
- "Help me relax my entire body systematically"

**Duration Recommendations:**
- **5-7 minutes:** Targeted tension relief (specific body areas)
- **10-15 minutes:** Full-body progressive relaxation

**Pro Tip:** Do these sessions lying down or in a comfortable seated position. Actually follow the body guidance - tense and release muscles as instructed.

---

### 5. Motivation

**Best for:**
- Feeling stuck or unmotivated
- Overcoming procrastination
- Low energy or drive
- Self-doubt and imposter syndrome
- Need for a confidence boost

**Session Style:**
- Energizing, empowering voice
- Cognitive reframing
- Connection to personal strengths and past victories
- Future-focused encouragement
- Activating rather than calming

**When to Use:**
- Morning motivation before a big day
- When procrastinating on important tasks
- After setbacks or failures
- When doubting your capabilities
- Start of new projects or challenges

**Sample Prompt Ideas:**
- "I'm procrastinating on a project and need a kick in the ass"
- "I feel like I'm not good enough and want to quit"
- "I need energy and drive to tackle my goals"
- "Help me remember why I'm capable and strong"

**Duration Recommendations:**
- **1-3 minutes:** Quick confidence boost
- **5-7 minutes:** Deep motivational reset
- **10+ minutes:** Comprehensive mindset shift

**Pro Tip:** Listen to motivation sessions in the morning or before tackling challenging tasks, not at bedtime.

---

### 6. Memory & Focus

**Best for:**
- Cognitive enhancement
- Improving concentration
- Memory retention
- Mental clarity
- Pre-study or work focus

**Session Style:**
- Clear, focused narration
- Brain activation techniques
- Attention training exercises
- Memory encoding strategies
- Mental sharpness practices

**When to Use:**
- Before studying or learning new information
- When experiencing brain fog
- To improve focus for deep work
- During cognitive fatigue

**Sample Prompt Ideas:**
- "I need to focus for a 3-hour work session"
- "Help me retain information I'm about to study"
- "My brain feels foggy and I need clarity"
- "Prepare my mind for complex problem-solving"

**Duration Recommendations:**
- **3-5 minutes:** Quick focus activation
- **7-10 minutes:** Deep cognitive preparation

---

### 7. Fitness (Mental Preparation)

**Best for:**
- Pre-workout mental preparation
- Athletic performance enhancement
- Overcoming exercise resistance
- Building mind-muscle connection
- Sports psychology

**Session Style:**
- Energizing, powerful voice
- Visualization techniques
- Performance mindset activation
- Body-mind connection
- Motivational and grounding

**When to Use:**
- 5-15 minutes before workouts
- Before athletic competitions or races
- When you're dreading a workout
- To mentally prepare for physical challenges

**Sample Prompt Ideas:**
- "I'm training for a marathon and need mental toughness"
- "Help me connect mind and body for my workout"
- "I don't feel like working out today - motivate me"
- "Prepare me for a challenging CrossFit session"

**Duration Recommendations:**
- **3-5 minutes:** Quick pre-workout activation
- **7-10 minutes:** Comprehensive mental preparation

**Pro Tip:** Listen right before your workout, not hours in advance. Let the session transition directly into physical activity.

---

## Premium Categories

### 8. ZEN AF (Premium)

**What makes it different:**
ZEN AF is VoiceThera's **raw, unfiltered, no-BS therapeutic category**. If standard meditation feels too soft or corporate-wellness-y, ZEN AF is for you.

**Best for:**
- People who don't respond to traditional "woo-woo" meditation
- Those who need tough love, not gentle encouragement
- Anyone tired of sanitized wellness content
- Seeking authentic, direct therapeutic language
- Wanting real talk about real problems

**Session Style:**
- Direct, authentic voice
- Street-level language (not profane, but real)
- Calls out unhealthy patterns bluntly
- Compassionate but confrontational
- No spiritual fluff - practical psychological tools

**When to Use:**
- When you need someone to tell you the truth you're avoiding
- After you've been lying to yourself
- When traditional meditation feels fake or ineffective
- For breakthrough moments requiring hard honesty

**Sample Prompt Ideas:**
- "I'm people-pleasing my way into burnout and need to stop"
- "I'm lying to myself about this relationship and I know it"
- "Stop letting my fear make decisions for me"
- "I need someone to tell me what I already know but won't admit"

**Sample ZEN AF Language:**
> "Your people-pleasing isn't kindness - it's cowardice dressed up as virtue. Every time you say yes when you mean no, you're teaching people you're a doormat. Your resentment isn't their fault; it's yours for refusing to have a spine. Today that ends."

**Duration Recommendations:**
- **5-7 minutes:** Targeted confrontation of specific pattern
- **10-15 minutes:** Deep therapeutic breakthrough work

**Pro Tip:** ZEN AF sessions can be emotionally intense. They're designed to break through denial and resistance. Use when you're ready for truth, not when you need comfort.

---

### 9. News Meditation (Premium)

**What makes it different:**
News Meditation transforms **current events and daily news** into mindfulness practice, helping you process information without overwhelm.

**Best for:**
- News-induced anxiety or doom-scrolling
- Staying informed without emotional burnout
- Processing upsetting world events
- Maintaining perspective on current events
- Political stress and polarization

**Session Style:**
- Grounding, perspective-giving voice
- Acknowledges real events (not toxic positivity)
- Helps metabolize information emotionally
- Practices healthy information consumption
- Connects personal agency to global issues

**When to Use:**
- After consuming upsetting news
- Morning news check-ins
- Before bed if news is affecting sleep
- During politically charged periods
- When feeling helpless about world events

**Sample Prompt Ideas:**
- "Climate news is making me feel hopeless and paralyzed"
- "I doom-scroll political news and it destroys my mental health"
- "I want to stay informed but not overwhelmed"
- "Help me process [specific current event] without spiraling"

**Sample News Meditation Language:**
> "Yes, the world is on fire. Yes, the news is objectively terrible. And yes, you're still allowed to make your kid breakfast and smile. Your capacity to hold both the world's pain and your own joy - that's not ignorance. That's survival. That's how you stay in the fight long-term."

**Duration Recommendations:**
- **5-7 minutes:** Process specific news event
- **10-15 minutes:** Broader practice of healthy news consumption

**Pro Tip:** Generate News Meditation sessions after consuming difficult news, not before. Let them help you process and integrate, not pre-defend against information.

---

## Choosing the Right Category

### Decision Tree

**If you need ENERGY and ACTIVATION:**
→ Motivation or Fitness

**If you need CALM and RELIEF:**
→ Anxiety & Stress Relief, Meditation, or Massage

**If you need REST:**
→ Sleep

**If you need CLARITY:**
→ Memory & Focus

**If you need TRUTH:**
→ ZEN AF

**If you need NEWS PROCESSING:**
→ News Meditation

### Mixing Categories

You can use multiple categories for different needs:

**Example Daily Flow:**
- **Morning:** Motivation (5 min) - Start day with energy
- **Midday:** Anxiety Relief (3 min) - Reset during stressful afternoon
- **Evening:** Sleep (10 min) - Wind down for bed

**Example Crisis Support:**
- **Step 1:** Anxiety Relief (5 min) - Immediate nervous system regulation
- **Step 2:** ZEN AF (7 min) - Confront the truth of situation
- **Step 3:** Motivation (5 min) - Activate for action

## Sample vs Personalized Sessions

### Sample Sessions

**Every category includes free sample sessions:**

**Pros:**
- Free - no credits required
- Professionally scripted
- High quality therapeutic content
- Great for discovering categories
- Unlimited replays

**Cons:**
- Not personalized to your biography
- Generic scenarios, not your specific challenges
- Names/places are generic
- Less emotional resonance

**When to use samples:**
- Exploring new categories
- Trying VoiceThera before committing to personalization
- Sharing with friends to demonstrate platform
- When you don't have credits available

### Personalized Sessions

**AI-generated specifically for you:**

**Pros:**
- Uses your Personal Biography
- References your specific people, places, challenges
- Addresses your exact current situation
- Connects to your goals and values
- Deep emotional resonance
- Feels like it was written FOR YOU

**Cons:**
- Consumes credits (1 per minute)
- Requires completed biography
- Takes 30-60 seconds to generate

**When to use personalized:**
- Addressing your specific challenges
- When sample sessions feel too generic
- For deep therapeutic work
- Maximum impact and transformation

## Questionnaire Tips

Every personalized session includes a category-specific questionnaire. The better your answers, the better your session.

### General Questionnaire Best Practices

**Do:**
- Be specific about current situation
- Include recent events that triggered the need
- Express emotions honestly
- Reference relationships and context
- Connect to your goals from your biography

**Don't:**
- Give one-word answers
- Stay vague or general
- Censor your struggles
- Rush through questions
- Leave fields blank

### Category-Specific Questionnaire Tips

**Anxiety & Stress Relief:**
- Describe the trigger event in detail
- Specify physical symptoms (tight chest, racing heart, etc.)
- Name the fear or worry explicitly
- Include context about why this matters

**Motivation:**
- Explain what you're procrastinating on and why
- Describe past moments you overcame similar resistance
- Connect to bigger goals and why they matter
- Include self-doubt you're experiencing

**Sleep:**
- Specify what's keeping you awake (thoughts, anxiety, physical discomfort)
- Describe your evening and what led to this state
- Include tomorrow's worries if relevant
- Note physical environment (comfortable? Too hot? Noise?)

**ZEN AF:**
- Be brutally honest about patterns you're avoiding
- Name the lie you've been telling yourself
- Describe the consequence of continuing current path
- Include what you know you need to do but haven't

## Category Combination Strategies

### Sequential Sessions

Use categories in sequence for comprehensive support:

**Crisis → Calm → Clarity → Action:**
1. Anxiety Relief (5 min) - Regulate nervous system
2. Meditation (7 min) - Create mental space
3. Memory/Focus (5 min) - Activate problem-solving
4. Motivation (5 min) - Take action

**Burnout Recovery:**
1. ZEN AF (7 min) - Confront burnout honestly
2. Massage (10 min) - Release physical tension
3. Sleep (10 min) - Rest and recover
4. Motivation (5 min) - Re-engage with purpose (next day)

### Complementary Sessions

Pair categories for holistic support:

- **Fitness + Motivation** - Mental and physical activation
- **Anxiety + Sleep** - Calm during day, rest at night
- **News Meditation + Anxiety** - Process information, manage resulting stress
- **ZEN AF + Motivation** - Truth-telling followed by action activation

---

## Advanced Category Features

### Category Favorites

Mark categories you use most frequently:
1. Generate session in a category
2. Category automatically appears in "Favorites" after 3+ uses
3. Quick access from dashboard

### Category History

View all sessions by category:
1. Go to Session History
2. Filter by category
3. See patterns in your wellness practice
4. Identify most-used categories

### Category Credits Tracking

Track credit consumption by category:
1. Navigate to Credits → Usage History
2. View breakdown by category
3. Understand where your credits go
4. Optimize category usage

---

**Ready to explore categories?** Start with sample sessions, then dive deep with personalized content.

Need help? Browse other guides in the Help Center or contact support at support@voicethera.com
`;
